import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiThermometer, FiAlertTriangle } from 'react-icons/fi';

const RadiatorRepair = () => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>Radiator Repair Dallas | Cooling System Service | Paradise-Performance</title>
        <meta name="description" content="Dallas radiator repair & cooling system experts. Overheating fixes, radiator replacement, coolant flushes. Same-day service. Don't blow your engine! Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/radiator-repair" />
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-orange-600 to-red-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/assets/assets/images/new/radiator/517374-scaled.webp"
            alt="Radiator cooling system"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-6">Radiator & Cooling System Repair Dallas</h1>
          <p className="text-xl mb-8 max-w-3xl">
            Dallas heat + bad radiator = blown engine. Don't let a $300 radiator problem 
            turn into a $5,000 engine replacement. We fix cooling issues before they get expensive.
          </p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-orange-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100">
            Stop Overheating: (817) 382-2316
          </a>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-red-50 border-2 border-red-500 p-6 rounded-lg mb-8">
            <h2 className="text-2xl font-bold mb-4 text-red-700">
              <FiAlertTriangle className="inline mr-2" />WARNING: Texas Heat Kills Engines
            </h2>
            <p className="text-gray-700">
              It's 105°F outside. Your engine runs at 200°F normally. One cooling system failure 
              and you're looking at warped heads, blown gaskets, or worse. In Dallas summer, 
              your cooling system isn't optional—it's survival.
            </p>
          </div>
          
          <h3 className="text-2xl font-bold mb-6">Signs You're About to Have a Bad Day:</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <FiThermometer className="text-red-500 mt-1 mr-3" />
              <p>Temperature gauge creeping past normal</p>
            </div>
            <div className="flex items-start">
              <FiThermometer className="text-red-500 mt-1 mr-3" />
              <p>Sweet smell (coolant leak)</p>
            </div>
            <div className="flex items-start">
              <FiThermometer className="text-red-500 mt-1 mr-3" />
              <p>Green/orange puddles under car</p>
            </div>
            <div className="flex items-start">
              <FiThermometer className="text-red-500 mt-1 mr-3" />
              <p>Heat not working (yes, related)</p>
            </div>
            <div className="flex items-start">
              <FiThermometer className="text-red-500 mt-1 mr-3" />
              <p>Steam from hood</p>
            </div>
            <div className="flex items-start">
              <FiThermometer className="text-red-500 mt-1 mr-3" />
              <p>Coolant in oil (milky substance)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/radiator/audi-radiator-repair.webp"
                alt="Audi radiator repair service"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-bold">European Cooling System Experts</p>
              </div>
            </div>
            <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/assets/assets/images/new/radiator/audi-radiator-repair-1.webp"
                alt="Radiator diagnostic and repair"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-bold">Advanced Diagnostics</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Complete Cooling System Services</h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-2">Radiator Replacement</h3>
              <p className="text-gray-700">
                OEM-spec radiators that actually cool. Not the thin aluminum junk that fails in 1 years. 
                Most cars done same day. <span className="font-bold text-green-600">$299-599 installed</span>
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-2">Water Pump Service</h3>
              <p className="text-gray-700">
                The heart of your cooling system. We replace with quality pumps and new gaskets. 
                <span className="font-bold text-green-600">$350-650 most vehicles</span>
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-2">Thermostat Replacement</h3>
              <p className="text-gray-700">
                Stuck closed = overheating. Stuck open = no heat. Simple fix, big difference. 
                <span className="font-bold text-green-600">$150-250 installed</span>
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-bold mb-2">Coolant System Flush</h3>
              <p className="text-gray-700">
                Remove old coolant, scale, and debris. Fresh coolant with proper mix for Texas heat. 
                <span className="font-bold text-green-600">$149.99</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Why Cheap Radiators Fail in Texas</h2>
          <p className="text-lg text-gray-700 mb-6">
            That $89 radiator online? It's single-row, thin aluminum that can't handle 
            Dallas heat. We see them fail within months. Quality radiators cost more 
            but actually work when it's 110°F in traffic.
          </p>
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="font-semibold mb-2">Customer learned the hard way:</p>
            <p className="text-gray-700">
              Had cheap radiator installed elsewhere. Failed 3 months later on Central Expressway 
              in August. Overheated engine needed head gasket - $2,800. Should've bought the 
              good radiator for $450. Don't be that guy.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Don't Wait for Steam</h2>
          <p className="text-xl mb-8">Fix cooling problems before they become engine problems.</p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-orange-600 px-10 py-5 rounded-lg font-bold text-lg hover:bg-gray-100">
            <FiPhone className="inline mr-2" /> (817) 382-2316
          </a>
          <p className="mt-6"><FiMapPin className="inline mr-2" />305 Co Rd 3470
Paradise, TX 76073</p>
        </div>
      </section>
    </motion.div>
  );
};

export default RadiatorRepair;
